// J.1 — Approvals queue.
// One queue, multiple trigger kinds (contract_deviation,
// frame_cap_overrun later, min_margin_breach later, customer_amount_overrun later).
// Reuses the configurable approver engine — when a task is open and
// you're in approver_user_ids, it shows up here.
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, AlertCircle, ExternalLink, Filter, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/approval-tasks`;

const mono = (s, c, w, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: s,
  color: c || "var(--text, #f0f2f8)",
  fontWeight: w || 400,
  letterSpacing: ls,
});
const syne = (s, w, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: s,
  fontWeight: w || 700,
  letterSpacing: ls,
});

const KIND_LABEL = {
  contract_deviation: "Contract deviation",
  frame_cap_overrun: "Frame cap overrun",
  min_margin_breach: "Min margin breach",
  customer_amount_overrun: "Customer amount overrun",
};

const useTasks = (status, assignedToMe) =>
  useQuery({
    queryKey: ["approval-tasks", status, assignedToMe],
    queryFn: () => axios.get(`${API}?status=${status}&assigned_to_me=${assignedToMe ? "true" : "false"}`).then((r) => r.data),
    refetchInterval: 60_000,
  });

const useDecide = (action) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tid, note }) => axios.post(`${API}/${tid}/${action}`, { note }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approval-tasks"] });
    },
  });
};

const fmtMoney = (v, ccy = "EUR") => {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  const txt = n.toLocaleString(undefined, { minimumFractionDigits: ccy === "HUF" ? 0 : 2, maximumFractionDigits: ccy === "HUF" ? 0 : 2 });
  return ccy === "HUF" ? `${txt} Ft` : `€ ${txt}`;
};

export default function Approvals() {
  const [status, setStatus] = useState("open");
  const [assignedToMe, setAssignedToMe] = useState(true);
  const { data, isLoading, error } = useTasks(status, assignedToMe);
  const approve = useDecide("approve");
  const reject = useDecide("reject");

  const [noteFor, setNoteFor] = useState({}); // tid -> note text

  const submit = async (tid, kind) => {
    const note = (noteFor[tid] || "").trim();
    try {
      if (kind === "approve") {
        await approve.mutateAsync({ tid, note });
        toast.success("Approved");
      } else {
        if (!note) {
          toast.error("Rejection requires a note");
          return;
        }
        await reject.mutateAsync({ tid, note });
        toast.success("Rejected");
      }
      setNoteFor((p) => ({ ...p, [tid]: "" }));
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.message || det?.code || "Failed");
    }
  };

  if (error?.response?.status === 403) {
    return (
      <div data-testid="approvals-403" className="rounded-lg p-6" style={{ background: "rgba(220,53,69,0.06)", border: "1px solid rgba(220,53,69,0.30)" }}>
        <AlertCircle size={16} color="#ef4444" className="inline mr-2" />
        <span style={mono(12, "#ef4444", 700)}>Approvals are forwarder-internal only.</span>
      </div>
    );
  }

  const items = data?.items || [];

  return (
    <div data-testid="approvals-page">
      <header className="mb-3">
        <h1 style={syne("clamp(22px, 3vw, 28px)", 800, "-0.5px")} data-testid="view-title">
          <Inbox size={20} className="inline mr-2" />
          Approvals
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          J.1 — One queue for every approval trigger. Today: contract-rate deviations. Soon: frame caps, min-margin gates, customer-portal amount approvals.
        </div>
      </header>

      <div className="flex items-center gap-2 mb-3" data-testid="approvals-filters">
        <Filter size={12} color="#9ec3ec" />
        {["open", "approved", "rejected", "cancelled"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className="px-2 py-1 rounded"
            style={{
              background: status === s ? "rgba(24,95,165,0.15)" : "transparent",
              border: `1px solid ${status === s ? "rgba(24,95,165,0.50)" : "rgba(255,255,255,0.15)"}`,
              color: status === s ? "#9ec3ec" : "#94a3b8",
              ...mono(10, undefined, 700, "0.5px"),
              cursor: "pointer", textTransform: "uppercase",
            }}
            data-testid={`approvals-filter-${s}`}
          >{s}</button>
        ))}
        <label className="ml-3 inline-flex items-center gap-1" style={mono(10, "#c5cad8")}>
          <input
            type="checkbox"
            checked={assignedToMe}
            onChange={(e) => setAssignedToMe(e.target.checked)}
            data-testid="approvals-assigned-to-me"
          />
          Only mine
        </label>
        <span className="ml-auto" style={mono(10, "#94a3b8")}>
          {items.length} task{items.length === 1 ? "" : "s"}
        </span>
      </div>

      {isLoading && <div style={mono(11, "#94a3b8")}><Loader2 size={12} className="inline animate-spin mr-2" />Loading approvals…</div>}

      <div className="flex flex-col gap-2" data-testid="approvals-list">
        {items.length === 0 && !isLoading && (
          <div className="rounded-lg p-6 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", ...mono(11, "#94a3b8") }}>
            No {status} tasks.
          </div>
        )}
        {items.map((t) => (
          <div
            key={t.id}
            className="rounded-lg p-3"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}
            data-testid={`approval-task-${t.id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span style={{ ...mono(9, "#ef4444", 700, "0.5px"), textTransform: "uppercase", background: "rgba(239,68,68,0.10)", padding: "2px 6px", borderRadius: 3, border: "1px solid rgba(239,68,68,0.30)" }}>
                  {KIND_LABEL[t.kind] || t.kind}
                </span>
                {t.escalated && (
                  <span
                    data-testid={`approval-task-escalated-pill-${t.id}`}
                    style={{ ...mono(9, "#e6a700", 700, "0.5px"), textTransform: "uppercase", background: "rgba(230,167,0,0.12)", padding: "2px 6px", borderRadius: 3, border: "1px solid rgba(230,167,0,0.40)" }}
                  >
                    ⚠ Escalated · SLA breached
                  </span>
                )}
                {!t.escalated && t.due_at && (
                  <span
                    data-testid={`approval-task-due-${t.id}`}
                    style={{ ...mono(9, "#9ec3ec", 700, "0.5px"), textTransform: "uppercase", background: "rgba(24,95,165,0.10)", padding: "2px 6px", borderRadius: 3, border: "1px solid rgba(24,95,165,0.40)" }}
                  >
                    ⏱ Due {t.due_at.substring(0, 16).replace("T", " ")}
                  </span>
                )}
                <span style={mono(11, "#9ec3ec", 700)}>{t.deal_id}</span>
                <Link to={`/logistics-requests/${t.deal_id}`} className="inline-flex items-center gap-1" style={mono(9, "#9ec3ec", 700)} data-testid={`approval-task-deal-link-${t.id}`}>
                  <ExternalLink size={10} /> Open deal
                </Link>
              </div>
              <span style={mono(9, "#94a3b8")}>
                {t.created_at ? t.created_at.substring(0, 16).replace("T", " ") : ""}
              </span>
            </div>
            {t.kind === "contract_deviation" && (
              <div className="grid grid-cols-3 gap-2 mb-2 p-2 rounded" style={{ background: "rgba(0,0,0,0.20)" }}>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Contract rate</div>
                  <div style={syne(18, 800, "-0.3px")} data-testid={`approval-task-expected-${t.id}`}>{fmtMoney(t.payload?.expected, "EUR")}</div>
                </div>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Entered price</div>
                  <div style={{ ...syne(18, 800, "-0.3px"), color: "#ef4444" }} data-testid={`approval-task-actual-${t.id}`}>{fmtMoney(t.payload?.actual, "EUR")}</div>
                </div>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Δ</div>
                  <div style={{ ...syne(18, 800, "-0.3px"), color: "#f3b14a" }}>
                    {t.payload?.expected && t.payload?.actual
                      ? `${(((Number(t.payload.actual) - Number(t.payload.expected)) / Number(t.payload.expected)) * 100).toFixed(2)}%`
                      : "—"}
                  </div>
                </div>
              </div>
            )}
            {t.kind === "frame_cap_overrun" && (
              <div className="mb-2 p-2 rounded" style={{ background: "rgba(0,0,0,0.20)" }}>
                <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Frame</div>
                <div style={syne(15, 700)}>{t.payload?.frame_name || t.payload?.frame_order_id}</div>
                <div style={mono(10, "#f3b14a", 700, "0.4px")} className="mt-1">Exceeds: {(t.payload?.exceed_dimensions || []).join(", ")}</div>
              </div>
            )}
            {t.kind === "min_margin_breach" && (
              <div className="grid grid-cols-3 gap-2 mb-2 p-2 rounded" style={{ background: "rgba(0,0,0,0.20)" }} data-testid={`approval-task-min-margin-${t.id}`}>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Min margin</div>
                  <div style={syne(18, 800, "-0.3px")} data-testid={`approval-task-mm-threshold-${t.id}`}>{t.payload?.threshold_pct != null ? `${Number(t.payload.threshold_pct).toFixed(2)} %` : "—"}</div>
                  <div style={mono(9, "#94a3b8")} className="mt-0.5">{(t.payload?.level || "—").toUpperCase()} level</div>
                </div>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Deal margin</div>
                  <div style={{ ...syne(18, 800, "-0.3px"), color: "#ef4444" }} data-testid={`approval-task-mm-actual-${t.id}`}>{t.payload?.actual_pct != null ? `${Number(t.payload.actual_pct).toFixed(2)} %` : "—"}</div>
                </div>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Δ</div>
                  <div style={{ ...syne(18, 800, "-0.3px"), color: "#f3b14a" }}>
                    {(t.payload?.threshold_pct != null && t.payload?.actual_pct != null)
                      ? `${(Number(t.payload.actual_pct) - Number(t.payload.threshold_pct)).toFixed(2)} pp`
                      : "—"}
                  </div>
                </div>
              </div>
            )}
            {t.kind === "customer_amount_overrun" && (
              <div className="grid grid-cols-3 gap-2 mb-2 p-2 rounded" style={{ background: "rgba(0,0,0,0.20)" }} data-testid={`approval-task-customer-amount-${t.id}`}>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Offer amount</div>
                  <div style={syne(18, 800, "-0.3px")} data-testid={`approval-task-cao-amount-${t.id}`}>{t.payload?.amount != null ? `${Number(t.payload.amount).toLocaleString()} ${t.payload?.currency || ""}`.trim() : "—"}</div>
                </div>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Threshold</div>
                  <div style={syne(18, 800, "-0.3px")} data-testid={`approval-task-cao-threshold-${t.id}`}>{t.payload?.threshold != null ? Number(t.payload.threshold).toLocaleString() : "—"}</div>
                </div>
                <div>
                  <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Δ Over</div>
                  <div style={{ ...syne(18, 800, "-0.3px"), color: "#e6a700" }}>
                    {(t.payload?.amount != null && t.payload?.threshold != null)
                      ? `+${(Number(t.payload.amount) - Number(t.payload.threshold)).toLocaleString()}`
                      : "—"}
                  </div>
                </div>
              </div>
            )}
            {t.payload?.justification && (
              <div className="mb-2 p-2 rounded" style={{ background: "rgba(243,177,74,0.06)", border: "1px solid rgba(243,177,74,0.30)" }}>
                <div style={{ ...mono(8, "#f3b14a", 700, "0.5px"), textTransform: "uppercase" }}>Operator justification</div>
                <div style={mono(11, "#f0f2f8")} data-testid={`approval-task-justification-${t.id}`}>{t.payload.justification}</div>
              </div>
            )}
            {t.status === "open" && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Note (required for reject, optional for approve)"
                  value={noteFor[t.id] || ""}
                  onChange={(e) => setNoteFor((p) => ({ ...p, [t.id]: e.target.value }))}
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#f0f2f8", ...mono(11), padding: "5px 8px", borderRadius: 4, flex: 1 }}
                  data-testid={`approval-task-note-${t.id}`}
                />
                <button
                  type="button"
                  onClick={() => submit(t.id, "approve")}
                  disabled={approve.isPending}
                  className="px-3 py-1.5 rounded inline-flex items-center gap-1"
                  style={{ background: "#0a8754", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }}
                  data-testid={`approval-task-approve-${t.id}`}
                >
                  <CheckCircle2 size={11} /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => submit(t.id, "reject")}
                  disabled={reject.isPending}
                  className="px-3 py-1.5 rounded inline-flex items-center gap-1"
                  style={{ background: "transparent", border: "1px solid rgba(226,75,74,0.55)", color: "#ef4444", ...mono(10, undefined, 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }}
                  data-testid={`approval-task-reject-${t.id}`}
                >
                  <XCircle size={11} /> Reject
                </button>
              </div>
            )}
            {t.status !== "open" && (t.decisions || []).length > 0 && (
              <div style={{ ...mono(10, "#94a3b8") }}>
                {t.decisions.map((d, i) => (
                  <div key={i}>
                    <span style={{ color: d.decision === "approve" ? "#0a8754" : "#ef4444", fontWeight: 700, textTransform: "uppercase" }}>{d.decision}</span>
                    {d.note ? ` · ${d.note}` : ""} · <span style={{ opacity: 0.6 }}>{(d.at || "").substring(0, 16).replace("T", " ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
